import { useMemo } from "react";

import { useTranslation } from "react-i18next";

import { InfoIcon } from "lucide-react";

import type { OpenAPI } from "simplydo/interfaces";
import { H2 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Message, MessageHeader } from "simplydo/react/ui/message";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "simplydo/react/ui/table";

import type {
  ImpactEffect,
  ImpactTypeKey,
  NumericalImpactsByType,
} from "./types";

export type IImpactMeasure = OpenAPI.Schemas["OrganisationImpact"] & {
  laneTotalImpactValue?: number;
  laneDropdownImpactValues?: { [key: string]: number };
};

const nameLookup: Record<ImpactTypeKey, string> = {
  time: "Time",
  people: "Staff / People",
  money: "Financial",
  plain: "Plain",
  numerical: "Numerical",
};

const ImpactStatistics = ({
  impactMeasures,
}: {
  impactMeasures: IImpactMeasure[];
}) => {
  const [numericalImpacts, dropdownImpacts] = useMemo(() => {
    const numericals: NumericalImpactsByType = {};
    const dropdowns: IImpactMeasure[] = [];

    impactMeasures.forEach((impact) => {
      if (!impact.laneTotalImpactValue && !impact.laneDropdownImpactValues) {
        return;
      }
      if (impact.type === "dropdown") {
        dropdowns.push(impact);
        return;
      }
      const effect = (impact.effect ?? "saving") as ImpactEffect;
      const impactType = (impact.type ?? "plain") as ImpactTypeKey;
      numericals[impactType] = numericals[impactType] ?? {};
      numericals[impactType]![effect] = numericals[impactType]![effect] ?? [];
      numericals[impactType]![effect]!.push(impact);
    });

    return [Object.entries(numericals), dropdowns] as const;
  }, [impactMeasures]);
  const { t } = useTranslation();

  if (numericalImpacts.length === 0 && dropdownImpacts.length === 0) {
    return (
      <Message color="blue">
        <Icon icon={InfoIcon} />
        <MessageHeader>
          No impacts assigned to {t("generic.ideas")}.
        </MessageHeader>
      </Message>
    );
  }

  return (
    <div style={{ flex: 1 }}>
      {dropdownImpacts.length === 0 ? null : (
        <>
          <H2>Categorical {t("generic.impacts")}</H2>
          <Table celled structured>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>
                  {t("common:capitalise", { key: "generic.impact" })}
                </TableHeaderCell>
                <TableHeaderCell>Name</TableHeaderCell>
                <TableHeaderCell>
                  Number of {t("generic.ideas")}
                </TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {dropdownImpacts.map((impact) => {
                const { laneDropdownImpactValues, name } = impact;
                const dropdownEntries = Object.entries(
                  laneDropdownImpactValues ?? {},
                );
                return dropdownEntries.map(([key, value], i) => (
                  <TableRow key={key}>
                    {i === 0 ? (
                      <TableCell collapsing rowSpan={dropdownEntries.length}>
                        <b>{name}</b>
                      </TableCell>
                    ) : null}
                    <TableCell>{key}</TableCell>
                    <TableCell>{String(value)}</TableCell>
                  </TableRow>
                ));
              })}
            </TableBody>
          </Table>
        </>
      )}

      {numericalImpacts.length === 0 ? null : (
        <>
          <H2>Annual costs and savings</H2>
          These are the total annual costs and savings across all{" "}
          {t("generic.ideas")} in your organisation.
          <Table celled structured>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Type</TableHeaderCell>
                <TableHeaderCell>Saving</TableHeaderCell>
                <TableHeaderCell collapsing textAlign="right">
                  Saving amount
                </TableHeaderCell>
                <TableHeaderCell>Cost</TableHeaderCell>
                <TableHeaderCell collapsing textAlign="right">
                  Cost amount
                </TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {numericalImpacts.flatMap(([impactType, impactValues]) => {
                const savings = impactValues.saving ?? [];
                const costs = impactValues.cost ?? [];

                const count = Math.max(savings.length, costs.length);

                const rows = [];
                for (let i = 0; i < count; i++) {
                  const saving = savings[i];
                  const cost = costs[i];

                  const savingValue = saving?.laneTotalImpactValue;
                  const costValue = cost?.laneTotalImpactValue;
                  const typeKey = impactType as ImpactTypeKey;

                  rows.push(
                    <TableRow key={(cost || saving)?._id}>
                      {i === 0 ? (
                        <TableCell collapsing rowSpan={count}>
                          <b>{nameLookup[typeKey] ?? impactType}</b>
                        </TableCell>
                      ) : null}
                      <TableCell>{saving?.name}</TableCell>
                      <TableCell
                        collapsing
                        textAlign="right"
                        style={
                          saving ? { backgroundColor: "#eeffee" } : undefined
                        }
                      >
                        {typeof savingValue === "number"
                          ? savingValue?.toLocaleString()
                          : savingValue}
                      </TableCell>
                      <TableCell>{cost?.name}</TableCell>
                      <TableCell
                        collapsing
                        textAlign="right"
                        style={
                          cost ? { backgroundColor: "#ffeeee" } : undefined
                        }
                      >
                        {cost ? `-` : null}
                        {typeof costValue === "number"
                          ? costValue?.toLocaleString()
                          : costValue}
                      </TableCell>
                    </TableRow>,
                  );
                }

                const totalCostSavings =
                  savings.reduce(
                    (acc: number, cur: IImpactMeasure) =>
                      acc + (cur.laneTotalImpactValue ?? 0),
                    0,
                  ) -
                  costs.reduce(
                    (acc: number, cur: IImpactMeasure) =>
                      acc + (cur.laneTotalImpactValue ?? 0),
                    0,
                  );

                const totalCostSavingsValue = totalCostSavings;

                rows.push(
                  <TableRow key={`${impactType}-total`}>
                    <TableCell
                      textAlign="right"
                      style={{ backgroundColor: "#f9fafb" }}
                    >
                      <b>Total:</b>
                    </TableCell>
                    <TableCell
                      colSpan={4}
                      style={{ backgroundColor: "#f9fafb" }}
                    >
                      <b>
                        {typeof totalCostSavingsValue === "number"
                          ? totalCostSavingsValue?.toLocaleString()
                          : totalCostSavingsValue}
                      </b>
                    </TableCell>
                  </TableRow>,
                );

                return rows;
              })}
            </TableBody>
          </Table>
        </>
      )}
    </div>
  );
};

export default ImpactStatistics;
